import React, { createContext, useCallback, useContext, useState } from "react";
import PaywallModal from "@/components/PaywallModal";

/**
 * PaywallContext — central paywall trigger.
 *
 * Any component that wants to gate a premium feature does:
 *
 *   const { showPaywall } = usePaywall();
 *   if (!premium?.active) { showPaywall("lyrics"); return; }
 *
 * The provider mounts a single ``<PaywallModal />`` at the root, so
 * triggers from anywhere in the tree share the same takeover surface
 * (no stacking modals, no z-index races).
 *
 * Supported reasons:
 *   - "skip"           Out of hourly skips (6/hr for free)
 *   - "lyrics"         Lyrics panel locked behind Premium
 *   - "queue-reorder"  Queue drag-to-reorder locked behind Premium
 *   - "queue-add"      Manual queue add locked behind Premium (reserved)
 *   - "generic"        Fallback copy
 *
 * The reason drives the modal's headline + body copy via the
 * ``REASONS`` map inside PaywallModal.
 */
const PaywallContext = createContext(null);

export const PaywallProvider = ({ children }) => {
  const [reason, setReason] = useState(null);          // null = closed
  const [meta, setMeta] = useState(null);              // optional details (e.g. reset_at)

  const showPaywall = useCallback((r = "generic", m = null) => {
    setReason(r);
    setMeta(m);
  }, []);

  const closePaywall = useCallback(() => {
    setReason(null);
    setMeta(null);
  }, []);

  return (
    <PaywallContext.Provider value={{ reason, meta, isOpen: !!reason, showPaywall, closePaywall }}>
      {children}
      <PaywallModal />
    </PaywallContext.Provider>
  );
};

export const usePaywall = () => {
  const ctx = useContext(PaywallContext);
  if (!ctx) {
    // Soft fallback so a missing provider doesn't crash the player
    return {
      reason: null, meta: null, isOpen: false,
      showPaywall: () => {}, closePaywall: () => {},
    };
  }
  return ctx;
};
